import type { MeResponse } from '@ucp/web/lib';
import { ModuleLauncherTrigger } from './ModuleLauncher';
import {
  moduleById,
  modulesConfig,
  type ModuleDef,
  type ModuleId,
  type PortalOpsId,
} from './modulesConfig';

/**
 * Mobile tier-1 module navigation (PRO-0350; ADR-014 Amendment 7). Below `md`, tier-1
 * is a **burger** in the header that opens a left glass drawer (`ModuleLauncherTrigger`).
 * This replaces the earlier scroll-vs-launcher split — the drawer scales to any module
 * count and keeps the tier-2 task tabs always-visible (D2). Desktop tier-1 remains the
 * `ModuleRail` side rail (`hidden md:flex`).
 *
 * PRO-0440: for `L6_PLATFORM_ADMIN` the portal-ops entries are appended in the drawer.
 * On `/platform/*` no business module is marked active (MF-001).
 */
export interface ModuleNavProps {
  activeModuleId: ModuleId;
  showPortalOps?: boolean;
  activePortalOpsId?: PortalOpsId | null;
  /**
   * Test override — defaults to `modulesConfig`.
   * @internal Production shell must omit this prop; tests inject `MOCK_SIX_MODULE_REGISTRY`.
   */
  modules?: readonly ModuleDef[];
  /** PRO-0600 / ADR-014 A8 — grouped vs flat drawer (default off). */
  navRestructureEnabled?: boolean;
  /**
   * PRO-0740 / ADR-014 amendment (MF-2). When true, no module row is marked active
   * in the mobile drawer (the non-module `/account` route), mirroring the desktop
   * `ModuleRail` suppression.
   */
  suppressActiveModule?: boolean;
  /** PRO-3100 / ADR-014 A16 - the caller, for the drawer's My actions row. */
  user?: MeResponse;
}

export function ModuleNav({
  activeModuleId,
  showPortalOps = false,
  activePortalOpsId = null,
  modules: modulesProp,
  navRestructureEnabled = false,
  suppressActiveModule = false,
  user,
}: ModuleNavProps) {
  const modules = modulesProp ?? modulesConfig;
  const activeModule =
    modules.find((module) => module.id === activeModuleId) ?? moduleById(activeModuleId);

  return (
    <nav aria-label="Modules" className="md:hidden">
      <ModuleLauncherTrigger
        activeModule={activeModule}
        modules={modules}
        showPortalOps={showPortalOps}
        activePortalOpsId={activePortalOpsId}
        navRestructureEnabled={navRestructureEnabled}
        suppressActiveModule={suppressActiveModule}
        {...(user ? { user } : {})}
      />
    </nav>
  );
}
